import dayjs from "dayjs";

// Patient-type rules shared by Billing (PatientsInfo.jsx) and Appointments
// (NewAppointment.jsx). Both screens must agree, otherwise a booking made on one
// carries the wrong visit_type into the other.

export const DEFAULT_REVIEW_DURATION = 7;

// Review window in days, configured on the org's `review_patient` feature.
export const getReviewDuration = (organizationDetails) =>
  organizationDetails?.payload?.organization_features?.find(
    (f) =>
      f?.key === "review_patient" &&
      f?.active === true &&
      f?.category === "Appointment",
  )?.data?.review_duration || DEFAULT_REVIEW_DURATION;

// Latest appointment first. The API sorts by visit_date only, which is a plain
// date — two visits on the same day tie, and the backend then returns them in
// id-ascending order, so appointments[0] was the EARLIER visit of the day.
// That made a completed review look like a fresh "new" visit and the patient
// came back as "Review" again instead of "Old". Break the tie on id (newer
// appointments have higher ids).
export const sortAppointmentsLatestFirst = (appointments) =>
  [...(appointments || [])].sort((a, b) => {
    const byDate = String(b?.visit_date || "").localeCompare(
      String(a?.visit_date || ""),
    );
    if (byDate !== 0) return byDate;
    return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  });

const capitalizeFirstLetter = (str = "") =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

// New  — no previous appointment
// Review — last visit completed, was not itself a review, within the window
// Old  — review already used, or outside the window
// Otherwise (last appointment not completed yet) — that appointment's visit_type
export const getPatientType = (appointments, reviewDuration) => {
  if (!appointments?.length) return "New";

  // sorted here as well: callers may pass appointments that did not go
  // through a sorted query (e.g. from Redux, set by another screen)
  const lastAppointment = sortAppointmentsLatestFirst(appointments)[0];
  const status = lastAppointment?.status?.toLowerCase();

  // not completed yet — keep the type it was booked as
  if (
    lastAppointment?.visit_type &&
    status !== "completed" &&
    status !== "finished"
  ) {
    return capitalizeFirstLetter(lastAppointment?.visit_type);
  } else if (
    lastAppointment?.visit_type &&
    lastAppointment?.visit_type === "review" &&
    status === "completed"
  ) {
    // one free revisit only — the review has been used
    return capitalizeFirstLetter("Old");
  }

  const lastVisitDate = dayjs(lastAppointment?.visit_date).startOf("day");
  const today = dayjs().startOf("day");

  const daysDiff = today.diff(lastVisitDate, "day");
  const isWithinReviewWindow = daysDiff <= reviewDuration;

  if (status === "completed" && isWithinReviewWindow) {
    return "Review";
  }

  return "Old";
};
